/**
 * Result type pattern for operations that can fail
 * Provides type-safe error handling without exceptions
 */

import type { AppError } from './errors';

/**
 * Success result type
 */
interface SuccessResult<T> {
  readonly success: true;
  readonly data: T;
}

/**
 * Failure result type
 */
interface FailureResult<E = AppError> {
  readonly success: false;
  readonly error: E;
}

/**
 * Result union type for operations that can fail
 */
export type Result<T, E = AppError> = SuccessResult<T> | FailureResult<E>;

/**
 * Create a success result
 */
export function success<T>(data: T): SuccessResult<T> {
    return { success: true, data };
}

/**
 * Create a failure result
 */
export function failure<E = AppError>(error: E): FailureResult<E> {
    return { success: false, error };
}

/**
 * Check if result is success
 */
export function isSuccess<T, E>(result: Result<T, E>): result is SuccessResult<T> {
    return result.success === true;
}

/**
 * Check if result is failure
 */
export function isFailure<T, E>(result: Result<T, E>): result is FailureResult<E> {
    return result.success === false;
}

/**
 * Map over a successful result
 */
export function map<T, U, E>(result: Result<T, E>, fn: (data: T) => U): Result<U, E> {
    if (isSuccess(result)) {
        return success(fn(result.data));
    }
    return result;
}

/**
 * FlatMap over a successful result
 */
export function flatMap<T, U, E>(result: Result<T, E>, fn: (data: T) => Result<U, E>): Result<U, E> {
    if (isSuccess(result)) {
        return fn(result.data);
    }
    return result;
}

/**
 * Get the data or throw the error
 */
export function unwrap<T, E extends Error>(result: Result<T, E>): T {
    if (isSuccess(result)) {
        return result.data;
    }
    throw result.error;
}

/**
 * Get the data or return a default value
 */
export function unwrapOr<T, E>(result: Result<T, E>, defaultValue: T): T {
    if (isSuccess(result)) {
        return result.data;
    }
    return defaultValue;
}

/**
 * Wrap an async operation in a Result
 */
export async function tryCatch<T, E = Error>(
    fn: () => Promise<T>,
    errorMapper?: (error: unknown) => E
): Promise<Result<T, E>> {
    try {
        const data = await fn();
        return success(data);
    } catch (error) {
        if (errorMapper) {
            return failure(errorMapper(error));
        }
        return failure(error as E);
    }
}

